import React, { memo, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Container from "../components/Layout/Container";
import ContentBlock from "../components/Layout/ContentBlock";
import FilterMenu from "../components/Layout/FilterMenu";
import TableFilterBox from "../components/common/TableFilterBox";
import ContentValidation from "../components/common/ContentValidation";
import TableUi from "../components/Layout/TableUI";
import { FiMail } from "react-icons/fi";
import { DEFAULT_PER_PAGE } from "../helpers/default";
import useFetch from "../hooks/useFetch";
import { getOrder } from "../redux/Actions/orderAction";
import RangePickerComp from "../components/common/RangePickerComp";
import {
  InputSwitchComponent,
  TextInput,
  Pagination,
} from "../components/globals";
import { handleKeyDown2 } from "../helpers/FunctionsGlobal";
import {
  formatDatePlus,
  getDateFormat,
  prettyDateFormat,
} from "../helpers/Utils";

const initialFilters = {
  created_at: ["", ""],
  orders_id_order_cont: null,
  receiver_cont: null,
  mail_status_eq: null,
};

const defaultFilters = [
  {
    label: "Order no.",
    value: "order_id_order_cont_any",
  },
  {
    label: "Receiver",
    value: "receiver_cont",
  },
  {
    label: "Date",
    value: "created_at",
    type: "date",
  },
  {
    label: "Mail status",
    value: "mail_status_eq",
  },
];

export default function WarrantyMailsPage({ match }) {
  const dispatch = useDispatch();
  const modalRef = useRef();

  const [page, setPage] = useState(1);
  const [perCount, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [filters, setFilters] = useState(initialFilters);

  const generateParams = () => {
    let filtStr = Object.entries(filters)
      .filter(([key, value]) => {
        if (!value) return false;

        if (key === "created_at") {
          const [startDate, endDate] = value;
          const filters = [];

          if (startDate) filters.push(`q[${key}_gteq]=${startDate}`);
          if (endDate)
            filters.push(`q[${key}_lteq]=${formatDatePlus(endDate)}`);

          return filters.length > 0;
        }

        if (typeof value === "string") {
          value = value.trim();
          return value.length > 0;
        }

        return typeof value === "string";
      })
      .map(([key, value]) => {
        if (key === "created_at") {
          const [startDate, endDate] = value;
          const filters = [];

          if (startDate) filters.push(`q[${key}_gteq]=${startDate}`);
          if (endDate)
            filters.push(`q[${key}_lteq]=${formatDatePlus(endDate)}`);

          return filters.join("&");
        }

        return `q[${key}]=${value}`;
      })
      .join("&");
    if (filtStr !== "") {
      filtStr = "&" + filtStr;
    }
    return filtStr;
  };

  const { value, loading, error, setReFetch } = useFetch(
    `/api/v1/warranty_mails?page=${page}&per_page=${perCount}${generateParams()}`,
    {},
    [page, perCount]
  );

  console.log({ value, loading, error });

  const openOrder = useCallback((order) => {
    dispatch(getOrder(order.id));
    modalRef.current.openModal("OrderShowModal");
  }, []);

  return (
    <Container
      location={{
        locationName: "Warranty mails",
        locationUrl: match.url,
      }}
    >
      <ContentBlock>
        <FilterMenu
          page={page}
          totals={value?.totals}
          length={value?.data?.length}
          text2="Mails"
          iconComponent={
            <FiMail
              color="#CACACA"
              style={{
                width: 32,
                height: 32,
              }}
            />
          }
          onChangeCountPage={setPerPage}
          countPage={perCount}
        />
      </ContentBlock>
      <ContentBlock containerClass="py-1">
        <TableFilterBox
          add={false}
          filter={true}
          onFilter={() => setReFetch()}
          clearFilter={() => setFilters(initialFilters)}
        >
          {defaultFilters.map((filter, idx) => (
            <div className={`px-1`} key={filter.value}>
              {filter?.label == "Mail status" ? (
                <InputSwitchComponent
                  type="multiple"
                  label={filter.label}
                  name={filter.value}
                  options={[
                    {
                      label: "All",
                      value: "",
                    },
                    {
                      label: "Delivered",
                      value: "delivered",
                    },
                    {
                      label: "Opened",
                      value: "opened",
                    },
                    {
                      label: "Not delivered",
                      value: "not_delivered",
                    },
                  ]}
                  value={filters[filter.value]}
                  onChange={({ target }) => {
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: target.value,
                    }));
                  }}
                />
              ) : filter?.type == "date" ? (
                <RangePickerComp
                  label={filter.label}
                  style={{ width: 250 }}
                  value={
                    filters[filter.value]
                      ? [
                          getDateFormat(filters[filter.value][0]),
                          getDateFormat(filters[filter.value][1]),
                        ]
                      : []
                  }
                  onChange={(_, args) => {
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: args,
                    }));
                  }}
                />
              ) : (
                <TextInput
                  text={filter.label}
                  name={filter.value}
                  style={{ width: 165 }}
                  value={filters[filter.value] || ""}
                  onKeyDown={handleKeyDown2(setReFetch)}
                  onChange={(val) => {
                    const value = val.target.value;
                    setFilters((prev) => ({
                      ...prev,
                      [filter.value]: value,
                    }));
                  }}
                />
              )}
            </div>
          ))}
        </TableFilterBox>
        {error ? (
          <div>{JSON.stringify(error)}</div>
        ) : (
          <ContentValidation loader={loading} array={value?.data}>
            <>
              <Table data={value?.data} openOrder={openOrder} />
              <div className="table-responsive">
                <Pagination
                  page={page}
                  totals={value?.totals || 0}
                  setPagination={setPage}
                  pageCount={perCount}
                />
              </div>
            </>
          </ContentValidation>
        )}
      </ContentBlock>
    </Container>
  );
}

const Table = memo(({ data = [], openOrder }) => {
  return (
    <div className="table-responsive">
      <TableUi
        className={"table-striped"}
        ths={[
          "Order no.",
          "Receiver",
          "Email",
          "Mail sent",
          "Last status",
          "Last status date",
        ]}
      >
        {data.map(
          ({
            id,
            order,
            receiver,
            email,
            created_at,
            mail_status,
            updated_at,
          }) => {
            return (
              <TableUi.tr key={id}>
                <TableUi.td className="p-left">
                  <div
                    className="cursor-pointer"
                    onClick={() => openOrder(order)}
                  ></div>
                  {order.id_order}
                </TableUi.td>
                <TableUi.td>{receiver}</TableUi.td>
                <TableUi.td>{email}</TableUi.td>
                <TableUi.td>{prettyDateFormat(created_at)}</TableUi.td>
                <TableUi.td>
                  <div className="d-flex align-items-center">
                    <div
                      className="mr-2"
                      style={{
                        width: 10,
                        height: 10,
                        background:
                          mail_status == "not_delivered"
                            ? "#DC0032"
                            : mail_status == "delivered"
                            ? "white"
                            : "#5CB800",
                        borderRadius: 9999,
                        border: `1px ${
                          mail_status == "not_delivered" ? "#DC0032" : "#5CB800"
                        } solid`,
                      }}
                    />
                    <div>
                      {mail_status.charAt(0).toUpperCase()}
                      {mail_status.slice(1).replace("_", " ")}
                    </div>
                  </div>
                </TableUi.td>
                <TableUi.td>{prettyDateFormat(updated_at)}</TableUi.td>
              </TableUi.tr>
            );
          }
        )}
      </TableUi>
    </div>
  );
});
